import React, { useEffect, useMemo, useState } from 'react';
import { errorMessage, listCountries, saveBasicSettingsWithWarnings, saveRegionSettings, updateTargetGroup } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { addDays, timeZoneLabel, toLocalInput } from '../../lib/dates';
import type { InputChange } from '../../types';
import { Button, Card, CardBody, ErrorState, InlineAlert, SelectField, Skeleton, TextField, useToast } from '../../ui';
import { GENDERS, LIMITS, basicsFromSaved, basicsToPayload, basicsWarnings, defaultBasics, moveStart, rollStaleStart, validateBasics } from './basicsModel';
import { useLocation } from 'react-router-dom';
import { SURVEY_URL_EXAMPLE, surveyUrlError } from '../projects/surveyUrl';
import { useWizard } from './WizardContext';
import type { BasicsValues } from './WizardContext';

const ageOptions = Array.from({ length: LIMITS.MAX_AGE - LIMITS.MIN_AGE + 1 }, (_, i) => LIMITS.MIN_AGE + i);

export function StepBasics() {
  const { project, targetGroup, reload, goStep, setLiveBasics } = useWizard();
  const { notify } = useToast();
  const countries = useAsyncData<{ country_id: string | number; name: string }[]>((signal) => listCountries({ signal }), []);
  const saved = targetGroup.basicSetting;
  // Arriving right after a template was applied: its dates are old, so show them moved to the next available time.
  const location = useLocation();
  const fromTemplate = Boolean(saved) && Boolean(location.state && (location.state as { fromTemplate?: boolean }).fromTemplate);
  const [values, setValues] = useState<BasicsValues>(() => (saved ? (fromTemplate ? rollStaleStart(basicsFromSaved(saved)) : basicsFromSaved(saved)) : defaultBasics()));
  const [startEdited, setStartEdited] = useState(false); // has the user chosen the start themselves?
  // This group's own survey URL (optional): empty = the campaign's is used.
  const [surveyUrl, setSurveyUrl] = useState(targetGroup.surveyUrl || '');
  const [attempted, setAttempted] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, run] = useSubmitGuard();

  // Feed the live feasibility panel.
  useEffect(() => { setLiveBasics(values); }, [values, setLiveBasics]);

  // The past-start rule only applies to a start the user set: a saved group's start that was changed, or a new
  // group's start that was typed. The pre-filled default is moved forward on save instead (see submit).
  const startChanged = saved ? values.startDate !== basicsFromSaved(saved).startDate : startEdited;
  const errors = useMemo(() => validateBasics(values, { startChanged }), [values, startChanged]);
  const warnings = basicsWarnings(values);
  const shown = (key: keyof BasicsValues) => (attempted ? errors[key] : undefined);

  const set = (name: keyof BasicsValues, value: string) => setValues((v) => ({ ...v, [name]: value }));
  const onChange = (e: InputChange) => set(e.target.name as keyof BasicsValues, e.target.value);

  // Moving the start carries the end with it, so the length of the field period is kept (and stays within 30 days).
  const onStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStartEdited(true);
    setValues((v) => moveStart(v, value));
  };

  // Picker limits: no start before now when creating; the end must follow the start by at most 30 days.
  const startMin = saved ? undefined : toLocalInput(new Date());
  const startDateObj = new Date(values.startDate);
  const endMin = Number.isNaN(startDateObj.getTime()) ? undefined : values.startDate;
  const endMax = Number.isNaN(startDateObj.getTime()) ? undefined : toLocalInput(addDays(startDateObj, LIMITS.MAX_FIELD_DAYS));

  const onMinAgeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const min = e.target.value;
    setValues((v) => ({ ...v, ageMinLimit: min, ageMaxLimit: Number(v.ageMaxLimit) < Number(min) ? min : v.ageMaxLimit }));
  };

  const countryChanged = saved != null && String(saved.country_id) !== String(values.country_id);
  const hasSavedRegions = Boolean(targetGroup.region);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    setFormError('');
    // A default start the user never touched may be in the past by now: move it (and the end) forward, then validate.
    const candidate = saved || startEdited ? values : rollStaleStart(values);
    if (candidate !== values) setValues(candidate);
    if (Object.keys(validateBasics(candidate, { startChanged })).length || surveyUrlError(surveyUrl)) return undefined;
    return run(async () => {
      try {
        const { warnings: serverWarnings } = await saveBasicSettingsWithWarnings(project.id, targetGroup.id, basicsToPayload(candidate));
        const urlNow = surveyUrl.trim();
        if (urlNow !== (targetGroup.surveyUrl || '')) await updateTargetGroup(project.id, targetGroup.id, { surveyUrl: urlNow || null }); // null = back to the campaign's
        // Regions belong to a country: if it changed, clear the old selection so it cannot go stale.
        if (countryChanged && hasSavedRegions) {
          await saveRegionSettings(project.id, targetGroup.id, { states: [], cities: [], wholeStates: [] });
        }
        await reload();
        notify('Basic settings saved', { tone: 'ok' });
        // Server advice (e.g. completes below the recommended 200) never blocks the save: it is shown once, then we move on.
        serverWarnings.forEach((w) => notify(w, { tone: 'info', duration: 8000 }));
        goStep('regions');
      } catch (err) {
        setFormError(errorMessage(err));
      }
    });
  };

  return (
    <form onSubmit={submit} noValidate>
      <Card>
        <CardBody>
          <h2 className="text-base font-semibold text-ink">Basic settings</h2>
          {fromTemplate && <div className="mt-3"><InlineAlert tone="info">Started from a template. Check the dates (moved to the next available time if the template's were in the past), then save.</InlineAlert></div>}
          <p className="mb-5 mt-1 text-sm text-muted">Define who you want to reach and how long the survey takes.</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Start date & time" required type="datetime-local" name="startDate" value={values.startDate} onChange={onStartChange} error={shown('startDate')} min={startMin} step={60} hint={`Your time zone: ${timeZoneLabel()}`} />
            <TextField label="End date & time" required type="datetime-local" name="endDate" value={values.endDate} onChange={onChange} error={shown('endDate')} min={endMin} max={endMax} step={60} hint={`At most ${LIMITS.MAX_FIELD_DAYS} days after the start.`} />

            <div className="sm:col-span-2">
              {countries.loading && !countries.data ? <Skeleton className="h-16 w-full" /> : countries.error ? (
                <ErrorState title="Could not load countries" message={errorMessage(countries.error)} onRetry={countries.reload} />
              ) : (
                <SelectField label="Country" required name="country_id" value={values.country_id} onChange={onChange} error={shown('country_id')} hint="Each country needs its own target group.">
                  <option value="">Select a country</option>
                  {(countries.data || []).map((c) => <option key={c.country_id} value={String(c.country_id)}>{c.name}</option>)}
                </SelectField>
              )}
            </div>

            <SelectField label="Minimum age" required name="ageMinLimit" value={values.ageMinLimit} onChange={onMinAgeChange} error={shown('ageMinLimit')}>
              {ageOptions.map((a) => <option key={a} value={String(a)}>{a}</option>)}
            </SelectField>
            <SelectField label="Maximum age" required name="ageMaxLimit" value={values.ageMaxLimit} onChange={onChange} error={shown('ageMaxLimit')}>
              {ageOptions.filter((a) => a >= Number(values.ageMinLimit || LIMITS.MIN_AGE)).map((a) => <option key={a} value={String(a)}>{a}</option>)}
            </SelectField>

            <SelectField label="Gender" name="gender" value={values.gender} onChange={onChange}>
              {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
            </SelectField>
            <TextField label="Length of interview (minutes)" required inputMode="numeric" name="estimatedLenghthOfInterview" value={values.estimatedLenghthOfInterview} onChange={onChange} error={shown('estimatedLenghthOfInterview')} hint={`${LIMITS.LOI.min}-${LIMITS.LOI.max} minutes`} />
            <TextField label="Wanted completes" required inputMode="numeric" name="noOfWantedCompletes" value={values.noOfWantedCompletes} onChange={onChange} error={shown('noOfWantedCompletes')} hint={warnings.noOfWantedCompletes || `${LIMITS.COMPLETES.min}-${LIMITS.COMPLETES.max.toLocaleString()}`} />
            <TextField label="Estimated incidence rate (%)" required inputMode="numeric" name="estimatedIncidenceRate" value={values.estimatedIncidenceRate} onChange={onChange} error={shown('estimatedIncidenceRate')} hint={`${LIMITS.IR.min}-${LIMITS.IR.max}%`} />
          </div>

          <div className="mt-4">
            <TextField label="Survey URL for this target group (optional)" type="url" inputMode="url" name="surveyUrl" placeholder={SURVEY_URL_EXAMPLE} value={surveyUrl} maxLength={2000} onChange={(e) => setSurveyUrl(e.target.value)} error={attempted ? surveyUrlError(surveyUrl) || undefined : undefined} hint={project.surveyUrl ? 'Leave empty to use the campaign\'s survey URL.' : 'Leave empty to use the campaign\'s survey URL (none is set yet).'} />
          </div>

          {countryChanged && hasSavedRegions && <div className="mt-4"><InlineAlert tone="warn">Changing the country will clear the regions you already selected for this target group.</InlineAlert></div>}
          {formError && <div className="mt-4"><InlineAlert>{formError}</InlineAlert></div>}

          <div className="mt-6 flex justify-end">
            <Button type="submit" loading={submitting}>Save & continue →</Button>
          </div>
        </CardBody>
      </Card>
    </form>
  );
}
